import { apiClient } from './api'
import type { Device, DeviceCreateDto, DeviceFilters, DeviceParameters, DeviceParametersUpdateDto } from '@/types/device.types'

export interface DeviceListResponse {
  devices: Device[]
  total: number
  page: number
  pageSize: number
}

export interface DeviceStatsResponse {
  totalDevices: number
  activeDevices: number
  devicesByType: Record<string, number>
  devicesByStatus: Record<string, number>
}

export const deviceService = {
  getDevices: async (filters?: DeviceFilters): Promise<DeviceListResponse> => {
    const { data } = await apiClient.get('/devices', { params: filters })
    return data
  },

  getDevice: async (deviceId: string): Promise<Device> => {
    const { data } = await apiClient.get(`/devices/${deviceId}`)
    return data
  },

  createDevice: async (device: DeviceCreateDto): Promise<Device> => {
    // Transform camelCase to snake_case for backend
    const payload = {
      name: device.name,
      serial_number: device.serialNumber,
      device_type: device.deviceType,
      account_id: device.accountId,
      modem_id: device.modemId,
      hardware_version: device.hardwareVersion,
      firmware_version: device.firmwareVersion,
    }
    const { data } = await apiClient.post('/devices', payload)
    return data
  },

  updateDevice: async (deviceId: string, device: Partial<DeviceCreateDto> & { lat?: number | null; long?: number | null; notes?: string }): Promise<Device> => {
    // Transform camelCase to snake_case for backend
    const payload: any = {}
    if (device.name !== undefined) payload.name = device.name
    if (device.serialNumber !== undefined) payload.serial_number = device.serialNumber
    if (device.deviceType !== undefined) payload.device_type = device.deviceType
    if (device.accountId !== undefined) payload.account_id = device.accountId
    if (device.modemId !== undefined) payload.modem_id = device.modemId
    if (device.hardwareVersion !== undefined) payload.hardware_version = device.hardwareVersion
    if (device.firmwareVersion !== undefined) payload.firmware_version = device.firmwareVersion
    if (device.lat !== undefined) payload.lat = device.lat
    if (device.long !== undefined) payload.long = device.long
    if (device.notes !== undefined) payload.notes = device.notes

    const { data } = await apiClient.put(`/devices/${deviceId}`, payload)
    return data
  },

  deleteDevice: async (deviceId: string): Promise<void> => {
    await apiClient.delete(`/devices/${deviceId}`)
  },

  archiveDevice: async (deviceId: string): Promise<void> => {
    await apiClient.post(`/devices/${deviceId}/archive`)
  },

  restoreDevice: async (deviceId: string): Promise<void> => {
    await apiClient.post(`/devices/${deviceId}/restore`)
  },

  toggleFavorite: async (deviceId: string): Promise<Device> => {
    const { data } = await apiClient.post(`/devices/${deviceId}/favorite`)
    return data
  },

  getFavoriteDevices: async (): Promise<DeviceListResponse> => {
    const { data } = await apiClient.get('/devices', {
      params: { favoritesOnly: true, pageSize: 100 }
    })
    return data
  },

  getDeviceStats: async (): Promise<DeviceStatsResponse> => {
    const { data } = await apiClient.get('/devices/stats')
    return data
  },

  getDeviceParameters: async (deviceId: string): Promise<DeviceParameters> => {
    const { data } = await apiClient.get(`/devices/${deviceId}/parameters`)
    return data
  },

  updateDeviceParameters: async (deviceId: string, params: DeviceParametersUpdateDto): Promise<DeviceParameters> => {
    const { data } = await apiClient.put(`/devices/${deviceId}/parameters`, params)
    return data
  },

  // Claim codes (super user only)
  getClaimCodes: async () => {
    const { data } = await apiClient.get('/devices/claim-codes')
    return data
  },

  generateClaimToken: async (deviceId: string) => {
    const { data } = await apiClient.post(`/devices/${deviceId}/claim`)
    return data
  },

  revokeClaimToken: async (deviceId: string) => {
    await apiClient.delete(`/devices/${deviceId}/claim`)
  },

  // Debug (super user only)
  getDeviceDebug: async (deviceId: string) => {
    const { data } = await apiClient.get(`/devices/${deviceId}/debug`)
    return data
  },

  uploadInstallPhoto: async (deviceId: string, file: File): Promise<{ installPic: string }> => {
    const formData = new FormData()
    formData.append('photo', file)
    const { data } = await apiClient.post(`/devices/${deviceId}/photo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    return data
  },

  getUserDevices: async (userId: string) => {
    const { data } = await apiClient.get(`/devices/user/${userId}`)
    return data.map((d: any) => ({
      id: d.id,
      name: d.name,
      serialNumber: d.serial_number,
      deviceType: d.device_type,
      status: d.status,
      batteryLevel: d.battery_level,
      lastSeen: d.last_seen,
      accountId: d.account_id,
    }))
  }
}
